import React, { useState } from 'react';
import { Phone, Mail, MapPin, CalendarDays, Clock, Send, Check, Facebook, Instagram, ChevronRight } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    serviceType: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState(null); // null, 'submitting', 'success', 'error'

  const serviceOptions = [
    { value: '', label: 'Select a Service' },
    { value: 'driveways', label: 'Driveway Cleaning' },
    { value: 'houses', label: 'House Washing' },
    { value: 'roof-softwashing', label: 'Roof Softwashing' },
    { value: 'fences', label: 'Fence Cleaning' },
    { value: 'paver-installation', label: 'Paver Installation' },
    { value: 'paver-sealing', label: 'Paver Sealing' },
    { value: 'other', label: 'Other (Please specify in message)' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('submitting');

    const serviceLabel = serviceOptions.find(o => o.value === formData.serviceType)?.label || formData.serviceType;

    const subject = encodeURIComponent(`Estimate Request from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Property Address: ${formData.address}\n` +
      `Service Needed: ${serviceLabel}\n\n` +
      `Additional Details:\n${formData.message || 'None provided'}`
    );

    window.location.href = `mailto:floboyspressurewashing@gmail.com?subject=${subject}&body=${body}`;

    setFormStatus('success');
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      serviceType: '',
      message: '',
    });

    setTimeout(() => {
      setFormStatus(null);
    }, 5000);
  };

  // FAQ data
  const faqs = [
    {
      id: 1,
      question: 'How soon can I get an estimate?',
      answer: 'We typically respond to estimate requests within 24-48 hours. For urgent requests, please call us directly.'
    },
    {
      id: 2,
      question: 'Do you offer free estimates?',
      answer: 'Yes, we provide free, no-obligation estimates for all our services.'
    },
    {
      id: 3,
      question: 'What areas do you serve?',
      answer: 'We serve the greater Orlando region, including all surrounding communities within a 25-mile radius.'
    },
    {
      id: 4,
      question: 'How do I prepare for your service?',
      answer: 'Before our arrival, please ensure all windows and doors are closed, remove any valuable or fragile items from the cleaning area, and secure pets indoors.'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Contact Hero Section */}
        <div className="bg-forest-700 py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-extrabold text-white sm:text-4xl md:text-5xl">
              Contact Us
            </h1>
            <p className="mt-4 text-xl text-forest-100 max-w-3xl mx-auto">
              Get in touch for a free estimate or to learn more about our services
            </p>
          </div>
        </div>

        {/* Contact Content */}
        <div className="py-12 md:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:grid lg:grid-cols-3 lg:gap-8">
              {/* Contact Info */}
              <div className="lg:col-span-1">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                  
                  <div className="space-y-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Phone className="h-6 w-6 text-forest-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Phone</h3>
                        <p className="mt-1">
                          <a 
                            href="tel:+14079063215" 
                            className="text-forest-600 hover:text-forest-500"
                          >
                            (407) 906-3215
                          </a>
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Mail className="h-6 w-6 text-forest-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Email</h3>
                        <p className="mt-1">
                          <a 
                            href="mailto:floboyspressurewashing@gmail.com" 
                            className="text-forest-600 hover:text-forest-500"
                          >
                            floboyspressurewashing@gmail.com
                          </a>
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <MapPin className="h-6 w-6 text-forest-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Service Area</h3>
                        <p className="mt-1 text-gray-600">
                          We proudly serve the greater Orlando region and surrounding communities.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <CalendarDays className="h-6 w-6 text-forest-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Scheduling</h3>
                        <p className="mt-1 text-gray-600">
                          Appointments available Monday through Saturday
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Clock className="h-6 w-6 text-forest-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">Business Hours</h3>
                        <p className="mt-1 text-gray-600">
                          Monday - Friday: 8:00 AM - 6:00 PM<br />
                          Saturday: 8:00 AM - 4:00 PM<br />
                          Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Connect With Us</h3>
                    <div className="flex space-x-4">
                      <a 
                        href="https://facebook.com/Floboys Surface Cleaning LLC" 
                        className="text-gray-400 hover:text-forest-600"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Facebook size={24} />
                      </a>
                      <a 
                        href="https://instagram.com/Floboys_surfacecleaning" 
                        className="text-gray-400 hover:text-forest-600"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Instagram size={24} />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Request Form */}
              <div className="mt-12 lg:mt-0 lg:col-span-2">
                <div className="bg-white shadow-lg rounded-lg overflow-hidden">
                  <div className="p-6 sm:p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Request a Free Estimate</h2>
                    
                    {formStatus === 'success' ? (
                      <div className="rounded-md bg-green-50 p-4 mb-6">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <Check className="h-5 w-5 text-green-500" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">
                              Estimate Request Submitted
                            </h3>
                            <div className="mt-2 text-sm text-green-700">
                              <p>
                                Thank you for your request! We&apos;ll review your information and contact you within 24-48 hours.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                              Full Name *
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="shadow-sm focus:ring-forest-500 focus:border-forest-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="Your name"
                                required
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Email *
                            </label>
                            <div className="mt-1">
                              <input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                className="shadow-sm focus:ring-forest-500 focus:border-forest-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="your.email@example.com"
                                required
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              Phone Number *
                            </label>
                            <div className="mt-1">
                              <input
                                type="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="shadow-sm focus:ring-forest-500 focus:border-forest-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="(407) 906-3215"
                                required
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700">
                              Service Needed *
                            </label>
                            <div className="mt-1">
                              <select
                                id="serviceType"
                                name="serviceType"
                                value={formData.serviceType}
                                onChange={handleChange}
                                className="shadow-sm focus:ring-forest-500 focus:border-forest-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                required
                              >
                                {serviceOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                              Property Address *
                            </label>
                            <div className="mt-1">
                              <input
                                type="text"
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                className="shadow-sm focus:ring-forest-500 focus:border-forest-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="Your street address, city, state, zip"
                                required
                              />
                            </div>
                          </div>
                          
                          <div className="sm:col-span-2">
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                              Additional Details
                            </label>
                            <div className="mt-1">
                              <textarea
                                id="message"
                                name="message"
                                rows={4}
                                value={formData.message}
                                onChange={handleChange}
                                className="shadow-sm focus:ring-forest-500 focus:border-forest-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                placeholder="Tell us about your project, specific areas that need attention, or any questions you have..."
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <button
                            type="submit"
                            className={`w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-forest-600 hover:bg-forest-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 ${
                              formStatus === 'submitting' ? 'opacity-75 cursor-not-allowed' : ''
                            }`}
                            disabled={formStatus === 'submitting'}
                          >
                            {formStatus === 'submitting' ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send className="mr-2 h-5 w-5" />
                                Submit Estimate Request
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                Frequently Asked Questions
              </h2>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
                Common questions about our estimate process and services
              </p>
            </div>
            
            <div className="mt-12 max-w-3xl mx-auto">
              <dl className="space-y-6">
                {faqs.map((faq) => (
                  <div key={faq.id} className="bg-white shadow rounded-lg p-6">
                    <dt className="text-lg font-bold text-gray-900">{faq.question}</dt>
                    <dd className="mt-2 text-gray-600">{faq.answer}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;